'use client'

// libraries
import { useEffect } from 'react'

// NLA-273: In print/PDF, wide study-plan rich-text tables (Word-pasted, with
// inline per-cell point widths + colspan) clip off the page edge. Instead of
// printing them inline, we pull every rich-text table out to a labelled
// "Vedlegg" (annex) section at the end of the document, rendered full-size on
// landscape pages, and leave a "Se vedlegg N" reference where the table sat.
//
// This only runs for print: the DOM is mutated on `beforeprint` and fully
// restored on `afterprint`, so the on-screen page is never changed. It follows
// the same direct-DOM-manipulation style as useTableWrapper / useAccordionTableWrapper.

interface PrintAnnexLabels {
    // "Se vedlegg {0}" — reference marker shown in the body flow
    reference?: string
    // "Vedlegg {0}" — heading of each table in the annex
    heading?: string
    // "Vedlegg" — heading of the annex section as a whole
    sectionTitle?: string
}

const ANNEX_CLASS = 'print-annex'
const REF_CLASS = 'print-annex-ref'
const HIDDEN_CLASS = 'print-annex-hidden'

export default function usePrintAnnex(labels?: PrintAnnexLabels) {
    const reference = labels?.reference || 'Se vedlegg {0}'
    const heading = labels?.heading || 'Vedlegg {0}'
    const sectionTitle = labels?.sectionTitle || 'Vedlegg'

    useEffect(() => {
        const format = (template: string, n: number) => template.replace('{0}', String(n))

        // Caption for a table = the title of the accordion it lives in, so the
        // reader can match "Se vedlegg N" to the right section.
        const getCaption = (table: Element): string => {
            const accordion = table.closest('.accordion')
            if (!accordion) return ''
            const titleEl = accordion.querySelector('.accordion__title-text span')
            return (titleEl?.textContent || '').trim()
        }

        const buildAnnex = () => {
            const page = document.querySelector('.page-course-plan')
            // guard against double-build (e.g. matchMedia + beforeprint both firing)
            if (!page || page.querySelector('.' + ANNEX_CLASS)) return

            // Collect the rich-text tables, skipping stray/empty ones. Word paste
            // often leaves tables with no real content; annexing those would add a
            // near-blank "Vedlegg N" page of their own at the end of the document.
            const tables: Element[] = []
            page.querySelectorAll('.table-rich-text').forEach((wrapper) => {
                const table = wrapper.querySelector('table')
                const text = table ? (table.textContent || '') : ''
                if (text.replace(/\s+/g, '').length > 0) {
                    tables.push(wrapper)
                }
            })
            if (tables.length === 0) return

            const annex = document.createElement('section')
            annex.className = ANNEX_CLASS

            const annexTitle = document.createElement('h2')
            annexTitle.className = ANNEX_CLASS + '__title h2'
            annexTitle.textContent = sectionTitle
            annex.appendChild(annexTitle)

            tables.forEach((table, i) => {
                const n = i + 1
                const caption = getCaption(table)

                // hide the original inline table for print
                table.classList.add(HIDDEN_CLASS)

                // reference marker (caption + "Se vedlegg N") in the body flow
                const ref = document.createElement('div')
                ref.className = REF_CLASS
                if (caption) {
                    const captionEl = document.createElement('p')
                    captionEl.className = REF_CLASS + '__caption'
                    captionEl.textContent = caption
                    ref.appendChild(captionEl)
                }
                const linkEl = document.createElement('p')
                linkEl.className = REF_CLASS + '__link'
                linkEl.textContent = format(reference, n)
                ref.appendChild(linkEl)
                table.parentNode?.insertBefore(ref, table)

                // full-size copy of the table in the annex, under its own heading
                const item = document.createElement('div')
                item.className = ANNEX_CLASS + '__item'

                const itemHeading = document.createElement('h3')
                itemHeading.className = ANNEX_CLASS + '__item-heading h3'
                itemHeading.textContent = caption
                    ? format(heading, n) + ' – ' + caption
                    : format(heading, n)
                item.appendChild(itemHeading)

                const clone = table.cloneNode(true) as HTMLElement
                clone.classList.remove(HIDDEN_CLASS)
                item.appendChild(clone)

                annex.appendChild(item)
            })

            page.appendChild(annex)
        }

        const teardownAnnex = () => {
            const page = document.querySelector('.page-course-plan')
            if (!page) return
            const annex = page.querySelector('.' + ANNEX_CLASS)
            if (annex) annex.remove()
            page.querySelectorAll('.' + REF_CLASS).forEach((ref) => ref.remove())
            page.querySelectorAll('.' + HIDDEN_CLASS).forEach((el) => el.classList.remove(HIDDEN_CLASS))
        }

        window.addEventListener('beforeprint', buildAnnex)
        window.addEventListener('afterprint', teardownAnnex)

        // Safari doesn't fire beforeprint/afterprint reliably — mirror via matchMedia
        const printMedia = typeof window.matchMedia === 'function' ? window.matchMedia('print') : null
        const handleMediaChange = (event: MediaQueryListEvent) => {
            if (event.matches) {
                buildAnnex()
            } else {
                teardownAnnex()
            }
        }
        if (printMedia?.addEventListener) {
            printMedia.addEventListener('change', handleMediaChange)
        }

        return () => {
            window.removeEventListener('beforeprint', buildAnnex)
            window.removeEventListener('afterprint', teardownAnnex)
            if (printMedia?.removeEventListener) {
                printMedia.removeEventListener('change', handleMediaChange)
            }
            teardownAnnex()
        }
    }, [reference, heading, sectionTitle])

    return null
}
